import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTracker } from '../context/TrackerContext';
import { adminLogin } from '../lib/api';

const EyeIcon: React.FC<{ show: boolean }> = ({ show }) => show ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-7 h-7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
  </svg>
);

const AdminLogin: React.FC = () => {
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [mounted, setMounted]   = useState(false);
  const { loginAsAdmin } = useTracker();
  const navigate = useNavigate();

  useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      await adminLogin(password);
      loginAsAdmin(password);
      toast.success('Admin access granted');
      navigate('/tracker/admin/students');
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg,#0d0b14 0%,#1a1228 50%,#0f0c1a 100%)' }}>

      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-80 h-80 rounded-full opacity-20" style={{ background: '#F0B429', filter: 'blur(100px)' }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-15" style={{ background: '#FF6535', filter: 'blur(100px)' }} />
        <div className="absolute top-1/3 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: '#7c3aed', filter: 'blur(80px)' }} />
        {/* Grid lines */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M60 0L0 0 0 60" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)"/>
        </svg>
      </div>

      <div className={`relative w-full max-w-sm mx-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

        {/* Gold glow behind card */}
        <div className="absolute -inset-2 rounded-3xl opacity-20 blur-2xl" style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }} />

        <div className="relative rounded-3xl overflow-hidden shadow-2xl border" style={{ background: 'rgba(255,255,255,0.97)', borderColor: 'rgba(240,180,41,0.3)' }}>

          {/* Gold accent bar */}
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg,#F0B429,#FF6535,#F0B429)' }} />

          {/* Header band */}
          <div className="px-8 pt-8 pb-6 text-center" style={{ background: 'linear-gradient(180deg,#fefce8 0%,#fff 100%)' }}>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-xl"
              style={{ background: 'linear-gradient(135deg,#F0B429,#d97706)' }}>
              <span style={{ color: '#2b2240' }}><ShieldIcon /></span>
            </div>
            <h1 className="text-2xl font-black" style={{ color: '#2b2240' }}>Admin Portal</h1>
            <div className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full" style={{ background: '#fef9c3' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[11px] font-bold" style={{ color: '#92400e' }}>Restricted Access</span>
            </div>
          </div>

          <div className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Password */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#2b2240' }}>Admin Password</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                    disabled={loading}
                    autoFocus
                    className="w-full pl-4 pr-11 py-3.5 border-2 rounded-xl text-sm outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed font-medium"
                    style={{ borderColor: password ? '#F0B429' : '#e5e7eb', color: '#1e1730' }}
                  />
                  <button type="button" onClick={() => setShowPass(s => !s)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors hover:bg-amber-50"
                    style={{ color: '#9ca3af' }}>
                    <EyeIcon show={showPass} />
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading || !password.trim()}
                className="w-full py-3.5 rounded-xl font-black text-sm transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg,#F0B429,#d97706)', color: '#2b2240' }}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Verifying…
                  </span>
                ) : 'Sign In as Admin'}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-3">
              <div className="flex-1 h-px bg-gray-100" />
              <a href="/tracker/mentor/login"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:bg-gray-50"
                style={{ color: '#9ca3af' }}>
                ← Mentor login
              </a>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
          </div>
        </div>

        <p className="text-center text-xs mt-4 font-medium" style={{ color: 'rgba(255,255,255,0.25)' }}>
          VistaTech Solutions · Admin Access
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
